import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import PlayingCard from "@/components/landing/PlayingCard";
import { Target, Sparkles } from "lucide-react";

export default function PairZeroOut() {
    const reduce = useReducedMotion();
    const { t } = useTranslation();
    const [zeroed, setZeroed] = useState(false);
    return (
        <section id="zero-out" className="py-16 sm:py-20 lg:py-28 bg-[#FBF7EE] relative overflow-hidden" data-testid="pair-zero-out-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-6">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#F4EBDD] border border-[#E9D9C1] px-3 py-1 text-xs font-semibold text-[#0B4A34] uppercase tracking-wider"><Target className="h-3.5 w-3.5" />{t("pairTrick.eyebrow")}</span>
                    <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#062A1E] leading-[1]">
                        {t("pairTrick.titleLine1")}<br />
                        <span className="text-[#137A55]">{t("pairTrick.titleLine2")}</span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#1B2621]/80 leading-relaxed max-w-lg">
                        {t("pairTrick.body_lead")} <span className="font-semibold text-[#0B4A34]">{t("pairTrick.body_strong")}</span> {t("pairTrick.body_tail")} <span className="font-semibold text-[#0B4A34]">{t("pairTrick.body_walkthrough")}</span>.
                    </p>
                    <ul className="mt-6 space-y-2.5 text-[#1B2621]/85">
                        {[1, 2, 3].map((i) => (
                            <li key={i} className="flex gap-2.5">
                                <Sparkles className="h-5 w-5 text-[#C8A24A] flex-shrink-0 mt-0.5" />
                                <span><span className="font-semibold">{t(`pairTrick.bullet${i}_title`)}</span> {t(`pairTrick.bullet${i}_body`)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-7">
                        <Button onClick={() => setZeroed((v) => !v)} className="bg-[#0B4A34] hover:bg-[#0F6244] text-[#FBF7EE] h-11 px-5 rounded-xl font-semibold" data-testid="zero-out-demo-button">
                            {zeroed ? t("pairTrick.demoButtonReset") : t("pairTrick.demoButton")}
                        </Button>
                    </div>
                </div>
                <div className="lg:col-span-6">
                    <div className="relative mx-auto w-full max-w-[440px] aspect-square rounded-3xl bg-[radial-gradient(closest-side,rgba(19,122,85,0.12),transparent_70%)] p-6">
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 h-full">
                            <div className="flex flex-col gap-3 sm:gap-4 relative">
                                <motion.div animate={reduce ? {} : zeroed ? { y: -4 } : { y: 0 }} transition={{ duration: 0.5 }} className="aspect-[3/4] relative"><PlayingCard rank="7" suit="♠" /></motion.div>
                                <motion.div animate={reduce ? {} : zeroed ? { y: 4 } : { y: 0 }} transition={{ duration: 0.5 }} className="aspect-[3/4] relative"><PlayingCard rank="7" suit="♥" red /></motion.div>
                                {zeroed && (
                                    <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45, ease: "backOut" }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="font-display text-7xl sm:text-8xl text-[#C8A24A] drop-shadow-[0_8px_24px_rgba(200,162,74,0.45)]">0</div>
                                    </motion.div>
                                )}
                            </div>
                            <div className="flex flex-col gap-3 sm:gap-4">
                                <div className="aspect-[3/4]"><PlayingCard rank="K" suit="♦" red /></div>
                                <div className="aspect-[3/4]"><PlayingCard faceDown /></div>
                            </div>
                            <div className="flex flex-col gap-3 sm:gap-4">
                                <div className="aspect-[3/4]"><PlayingCard faceDown /></div>
                                <div className="aspect-[3/4]"><PlayingCard rank="3" suit="♣" /></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
